import { Button, Flex, Image, Form, Input, Modal, Select } from "antd";
import { useGetTicketQuery,useAddTicketMutation } from "../../../../../../store/services/api/ticket-api";
import AssetFormControl from "../../../../../../components/form-controls/AssetFormControl";
import React, { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import {
  useGetAllSitesQuery,
  useGetAllTeamsQuery,
} from "../../../../../../store/services/api/site-api";
import SiteTeamSelector from "../../../../../../components/form-controls/SiteTeamSelector";
import useUser from "../../../../../../hooks/useUser";
import {FlatList} from "react-native";
import * as ImagePicker from "expo-image-picker";
import {compressImage} from "../../../../../../utils/imageUtils";
import {FileImageOutlined, CameraOutlined} from "@ant-design/icons";
import useSubmitOnEnter from "../../../../../../hooks/useSubmitOnEnter";

interface TicketForm {
  issue: string;
  contactEmail: string;
  assetId: string;
  entrySource: string;
  siteId?: string;
  teamId?: string;
  images?: string[];
}

interface CreateTicketModalProps {
  open: boolean;
  ticketId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function CreateTicketModal(props: CreateTicketModalProps) {
  const [form] = Form.useForm<TicketForm>();
  const [createTicket, { isLoading: createLoading }] = useAddTicketMutation();
  const { data: ticket } = useGetTicketQuery(props.ticketId, { skip: !props.ticketId });
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  const { data: sites } = useGetAllSitesQuery(null);
  const { data: allTeams } = useGetAllTeamsQuery(null);
  const [filteredTeams, setFilteredTeams] = useState<any[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const [disabled, setDisabled] = useState(true);
  const draggleRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const {user} = useUser();
  
  
  var isEdit = false;
  if (props.ticketId){
    isEdit = true;
  }
  const handleEnterSubmit = useSubmitOnEnter(form);
  
  useEffect(() => {
    const loadDefaults = () => {
      if (!sites || !allTeams) return;

      const pgcSite = sites.find((s: any) => s.name === "GC");
      if (!pgcSite) return;

      const pgcTeam = allTeams.find(
        (t: any) => t.siteId === pgcSite.id && t.name === "Infrastructure"
      );

      return { siteId: pgcSite.id, teamId: pgcTeam?.id };
    };

    if (props.ticketId && ticket) {
      const assetOption = ticket.asset
        ? { value: ticket.asset.id, label: ticket.asset.assetCode }
        : undefined;

      const defaults = loadDefaults();

      form.setFieldsValue({
        siteId: ticket.team?.siteId || defaults?.siteId,
        teamId: ticket.team?.id || defaults?.teamId,
        issue: ticket.issue,
        contactEmail: ticket.contactEmail,
        entrySource: ticket.entrySource,
        assets: assetOption,
        images: ticket.images
      });
      setSelectedImages(ticket.images);

      setSelectedSiteId(ticket.team?.siteId || defaults?.siteId);
      setFilteredTeams(
        allTeams.filter(
          (t: any) => t.siteId === (ticket.team?.siteId || defaults?.siteId)
        )
      );

    } else if (!props.ticketId) {
      form.resetFields();
      const defaults = loadDefaults();

      if (defaults) {
        form.setFieldsValue({
          siteId: defaults.siteId,
          teamId: defaults.teamId,
        });
        setSelectedImages([]);
        setSelectedSiteId(defaults.siteId);
        setFilteredTeams(
          allTeams.filter((t: any) => t.siteId === defaults.siteId)
        );
      }
    }
  }, [props.ticketId, ticket, sites, allTeams]);


  const convertFormData = async (data: TicketForm): Promise<any> => {
  const images = await Promise.all(
    selectedImages.map(async (uri) => {
      if (uri.startsWith("https://")) {
        return uri;
      }
      return compressImage(uri);
    })
  );

  return {
    ...data,
    assetId: (data as any).assets?.value,
    assets: undefined,
    id: props.ticketId,
    lastModifiedBy: user.id,
    images,
  };
};

  const handleOnFinish = async (data: TicketForm) => {
  const finalData = await convertFormData(data);

  createTicket(finalData)
    .unwrap()
    .then(() => {
      setSelectedImages([]);
      props.onSuccess?.();
      props.onClose();
    });
};

  const takeImage = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      alert("Permission to access the camera is required!");
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      base64: true,
    });

    if (!result.canceled) {
      setSelectedImages([...selectedImages, result.assets[0].uri]);
    }
  };

  const pickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      alert("Permission to access the photos is required!");
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      base64: true,
      allowsMultipleSelection: true
    });

    if (!result.canceled) {
      setSelectedImages([...selectedImages, result.assets[0].uri]);
    }
  };

  const deleteImage = (index) => {
    setSelectedImages(selectedImages.filter((_, idx) => idx !== index));
  };

  const handleOnSiteChange = (siteId: string | null) => {
    setSelectedSiteId(siteId);
    
    if (!siteId) {
      form.setFieldsValue({ teamId: undefined });
      setFilteredTeams([]);
      return;
    }

    if (allTeams) {
      const filtered = allTeams.filter((t: any) => t.siteId === siteId);
      setFilteredTeams(filtered);
      const currentTeamId = form.getFieldValue("teamId");
      if (!currentTeamId) {
        const defaultTeamName = filtered.find((t: any) =>
          t.name.includes("Infrastructure")
        )?.name; 
        const defaultTeam = filtered.find((t: any) => t.name === defaultTeamName);
        if (defaultTeam) {
          form.setFieldsValue({ teamId: defaultTeam.id });
        }
      }
    }
  };

  return (
    <Modal
  destroyOnClose={true}
  title={
    <div
      style={{
        cursor: "move",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
      }}
      onMouseOver={() => disabled && setDisabled(false)}
      onMouseOut={() => setDisabled(true)}
    >

      <Form.Item
        required
        shouldUpdate={(prev, curr) =>
          prev.siteId !== curr.siteId || prev.teamId !== curr.teamId
        }
      >
        {() => (
          <>
            <SiteTeamSelector
              sites={sites}
              teams={allTeams}
              value={{
                siteId: form.getFieldValue("siteId"),
                teamId: form.getFieldValue("teamId"),
              }}
              onChange={(siteId, teamId) => {
                form.setFieldsValue({ siteId, teamId });
                handleOnSiteChange(siteId);
              }}
            />
          </>
        )}
      </Form.Item>
      <span style={{marginLeft:4}}> {isEdit ? 'Edit Ticket' : 'Create Ticket'}</span>
    </div>
  }
  open={props.open}
  onCancel={props.onClose}
  onOk={() => form.submit()}
  confirmLoading={createLoading}
  centered={false}
  width={600}
  style={{
    top: position.y + 100,
    left: position.x + window.innerWidth / 2 - 300,
    margin: 0,
    transform: "none",
    position: "absolute",
    transition: "none",
  }}
  modalRender={(modal) => (
    <Draggable
      disabled={disabled}
      nodeRef={draggleRef}
      defaultPosition={{ x: 0, y: 0 }}
      onStart={() => setIsDragging(true)}
      onStop={() => setIsDragging(false)}
    >
      <div
        ref={draggleRef}
        style={{
          willChange: "transform",
          pointerEvents: isDragging ? "none" : "auto",
        }}
      >
        {modal}
      </div>
    </Draggable>
  )}
>
      <Form<TicketForm> form={form} onFinish={handleOnFinish} layout="vertical" onKeyDown={handleEnterSubmit}>
        
        <Form.Item name="issue" label="Issue" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item
          name="contactEmail"
          label="Contact Details"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="assets" label="Asset" rules={[{ required: true }]}>
          <AssetFormControl enableEdit={false} />
        </Form.Item>

        <Form.Item
          name="entrySource"
          label="Ticket Source"
          rules={[{ required: true, message: "Please select a ticket source" }]}
        >
          <Select>
            <Select.Option value="Email">Email</Select.Option>
            <Select.Option value="Phone">Phone</Select.Option>
            <Select.Option value="In Person">In Person</Select.Option>
            <Select.Option value="Other">Other</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="siteId" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="teamId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <FlatList
          horizontal
          data={selectedImages ?? []}
          renderItem={({item, index}) => (
            <Image
              preview={false}
              onClick={() => deleteImage(index)}
              style={{paddingInline: 2, objectFit: 'cover'}}
              key={`ticket-images-${index}`}
              src={item}
              width={96}
              height={96}
            />
          )}
        />

        <Flex gap={4} style={{paddingBlock: '24px'}}>
          <Button
            onClick={pickImage}
            disabled={selectedImages.length >= 5}
            size='large'
            icon={<FileImageOutlined />}
          />
          <Button
            onClick={takeImage}
            disabled={selectedImages.length >= 5}
            size='large'
            icon={<CameraOutlined />}
          />
        </Flex>
      </Form>
    </Modal>
  );
}
